import {
  authApi,
  type AuthResponse,
  type GenericErrorResponse,
} from "./authService";

export interface CalculatorCategory {
  category: string;
  description?: string;
  status?: string;
  // Optional fields populated by completeness API
  completeness_percentage?: number;
  completeness_status?: string;
}

/**
 * Narrows a column's options based on another column's value in the same row.
 *
 * Layered on top of `exact_values` rather than replacing it: a column with no
 * conditions, or whose parent is unset, falls back to plain `allowed_values`,
 * so every existing assessment is unaffected.
 *
 * Each entry names one parent column and one parent value. That is sufficient
 * for a chain as long as each link is unambiguous on its own — Procurement's
 * Size depends on Item Type alone because no item type is shared between
 * categories. A genuinely ambiguous chain would need composite conditions.
 */
export interface ConditionalAllowedValues {
  depends_on_column_id: number;
  when_matches: string;
  allowed_values: string[];
}

export interface FormColumn {
  id: number | string;
  label: string;
  description?: string;
  is_editable: boolean;
  is_required: boolean;
  constraints: {
    type: string;
    min_value?: number;
    max_value?: number;
    allowed_values?: string[];
    numeric_values?: number[];
    step_value?: number;
    conditional_allowed_values?: ConditionalAllowedValues[];
  };
}

export interface FormRow {
  id: number | string;
  label: string;
  label2?: string;
  description?: string;
  is_required: boolean;
  non_editable_columns?: number[] | string[];
}

export interface FormSchema {
  columns: FormColumn[];
  rows: FormRow[];
}

export interface FieldData {
  column_id: number | string;
  value: string | number | boolean | null;
  row_id?: number;
  dynamic_row_id?: string;
}

export interface FormDataResponse {
  [rowKey: string]: {
    [columnKey: string]: string | number | boolean;
  };
}

export type LabType = "wet" | "dry";
export type CalculatorLevel = "bronze" | "silver" | "gold";

export interface SubmissionResults {
  energy?: number;
  waste?: number;
  emissions?: number;
  cost?: number;
  storage?: number;
  volume_ml?: number;
  toxicity_burden?: number;
  [key: string]: number | undefined; // Allow other numeric keys
}

export interface LabWasteCalculationResults {
  annual_cost?: number;
  annual_emissions?: number;
  annual_waste_kg?: number;
  annual_energy_kwh?: number;
  total_emissions_kgco2e?: number;
  total_energy_mj?: number;
  total_annual_cost_aud?: number;
  total_annual_emissions_kgco2e?: number;
  total_annual_volume_ml?: number;
  total_annual_toxicity_burden?: number;
  // Procurement reports baseline, adjusted and avoided figures for each of
  // cost, waste and emissions. Only the baseline set is surfaced, matching what
  // the results panel shows after a reload.
  annual_units_procured?: number;
  baseline_annual_cost?: number;
  baseline_annual_waste_kg?: number;
  baseline_annual_emissions_kgco2e?: number;
  calculation_status?: string;
  country?: string;
  lab_id?: string;
  organisation_id?: string;
}

export interface LabWasteSubmissionResponse {
  calculations: LabWasteCalculationResults;
  success: boolean;
  message?: string;
  bronze_completed?: boolean;
  show_audit_message?: boolean;
}

export class CalculatorService {
  static async getCategories(
    labId: string,
    level: CalculatorLevel,
  ): Promise<CalculatorCategory[]> {
    const params = new URLSearchParams({
      level: level,
    });

    const response = (await authApi(
      `/calculator/categories/${labId}?${params}`,
    )) as
      | (AuthResponse & {
          categories?: CalculatorCategory[];
        })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch categories");
    }

    return response.categories ?? [];
  }

  static async getCalculatorStatus(
    labId: string,
    level: CalculatorLevel,
    category?: string,
  ): Promise<{ calculators: Array<{ category: string; status: string }> }> {
    const params = new URLSearchParams({
      level: level,
    });
    if (category) params.set("category", category);

    const url = `/calculator/status/${labId}${params.toString() ? `?${params.toString()}` : ""}`;
    const response = (await authApi(url)) as
      | (AuthResponse & {
          calculators?: Array<{ category: string; status: string }>;
        })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch calculator status");
    }

    return { calculators: response.calculators ?? [] };
  }

  static async getFormSchema(
    category: string,
    labId: string,
    level: CalculatorLevel,
  ): Promise<FormSchema> {
    const params = new URLSearchParams({
      level: level,
    });

    const response = (await authApi(
      `/calculator/schema/${labId}/${encodeURIComponent(category)}?${params}`,
    )) as
      | (AuthResponse & {
          schema?: FormSchema;
        })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch form schema");
    }

    const schema = response.schema;
    if (!schema) {
      throw new Error("Form schema missing from response");
    }

    return schema;
  }

  static async getFormData(
    category: string,
    labId: string,
    level: CalculatorLevel,
  ): Promise<FormDataResponse> {
    const params = new URLSearchParams({
      level: level,
    });

    const response = (await authApi(
      `/calculator/data/${labId}/${encodeURIComponent(category)}?${params}`,
    )) as
      | (AuthResponse & {
          form_data?: FormDataResponse;
        })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch form data");
    }

    return response.form_data ?? {};
  }

  static async getCalculatorCompleteness(
    labId: string,
    level: CalculatorLevel,
    category?: string,
  ): Promise<{
    completeness: Array<{
      category: string;
      completeness_percentage: number;
      status: string;
    }>;
  }> {
    const params = new URLSearchParams({
      level: level,
    });
    if (category) params.set("category", category);

    const url = `/calculator/completeness/${labId}${params.toString() ? `?${params.toString()}` : ""}`;
    const response = (await authApi(url)) as
      | (AuthResponse & {
          completeness?: Array<{
            category: string;
            completeness_percentage: number;
            status: string;
          }>;
        })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ||
          "Failed to fetch calculator completeness",
      );
    }

    type CompletenessItem = {
      category: string;
      completeness_percentage: number;
      status: string;
    };
    const completeness =
      (response as AuthResponse & { completeness?: CompletenessItem[] })
        .completeness ?? [];
    return { completeness };
  }

  static async saveFormData(
    category: string,
    labId: string,
    level: CalculatorLevel,
    fieldData: FieldData,
  ): Promise<{ id: string }> {
    const params = new URLSearchParams({
      level: level,
    });

    const response = (await authApi(
      `/calculator/save/${labId}/${encodeURIComponent(category)}?${params}`,
      {
        method: "POST",
        body: JSON.stringify({
          column_id: fieldData.column_id,
          value: fieldData.value,
          ...(fieldData.row_id && { row_id: fieldData.row_id }),
          ...(fieldData.dynamic_row_id && {
            dynamic_row_id: fieldData.dynamic_row_id,
          }),
        }),
      },
    )) as (AuthResponse & { id?: string }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to save form data");
    }

    const id = response.id;
    if (!id) {
      throw new Error("Saved form data missing identifier");
    }

    return { id };
  }

  static async deleteRowData(
    category: string,
    labId: string,
    level: CalculatorLevel,
    dynamicRowId: string,
  ): Promise<{
    deleted_count: number;
  }> {
    const params = new URLSearchParams({
      level: level,
    });

    const response = (await authApi(
      `/calculator/delete-row/${labId}/${encodeURIComponent(category)}?${params}`,
      {
        method: "DELETE",
        body: JSON.stringify({
          dynamic_row_id: dynamicRowId,
        }),
      },
    )) as (AuthResponse & { deleted_count?: number }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to delete row data");
    }

    const deletedCount = response.deleted_count;
    if (typeof deletedCount !== "number") {
      throw new Error("Delete row response missing deleted_count");
    }

    return { deleted_count: deletedCount };
  }

  static async submitCalculator(
    labId: string,
    category: string,
    level: CalculatorLevel,
    options?: { not_applicable?: boolean },
  ): Promise<SubmissionResults> {
    const params = new URLSearchParams({
      level: level,
    });

    const body =
      options && Object.prototype.hasOwnProperty.call(options, "not_applicable")
        ? { not_applicable: !!options.not_applicable }
        : undefined;
    type SubmitCalculatorResponse = AuthResponse & {
      results?: SubmissionResults;
      calculations?: LabWasteCalculationResults;
    };
    const response = (await authApi(
      `/calculator/${labId}/${encodeURIComponent(category)}?${params}`,
      {
        method: "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      },
    )) as SubmitCalculatorResponse | GenericErrorResponse;

    if (!("success" in response) || !response.success) {
      const errorResponse = response as GenericErrorResponse;
      throw new Error(errorResponse.error || "Failed to submit calculator");
    }

    const successResponse = response as SubmitCalculatorResponse;

    if (successResponse.calculations) {
      const calculations = successResponse.calculations;
      return {
        ...(typeof calculations.annual_cost === "number"
          ? { cost: calculations.annual_cost }
          : {}),
        ...(typeof calculations.annual_emissions === "number"
          ? { emissions: calculations.annual_emissions }
          : {}),
        ...(typeof calculations.annual_energy_kwh === "number"
          ? { energy: calculations.annual_energy_kwh }
          : {}),
        ...(typeof calculations.annual_waste_kg === "number"
          ? { waste: calculations.annual_waste_kg }
          : {}),
        ...(typeof calculations.total_energy_mj === "number"
          ? { energy_mj: calculations.total_energy_mj }
          : {}),
        ...(typeof calculations.total_emissions_kgco2e === "number"
          ? { emissions_kgco2e: calculations.total_emissions_kgco2e }
          : {}),
        ...(typeof calculations.total_annual_cost_aud === "number"
          ? { cost: calculations.total_annual_cost_aud }
          : {}),
        ...(typeof calculations.total_annual_emissions_kgco2e === "number"
          ? {
              emissions: calculations.total_annual_emissions_kgco2e,
              emissions_kgco2e: calculations.total_annual_emissions_kgco2e,
            }
          : {}),
        ...(typeof calculations.total_annual_volume_ml === "number"
          ? { volume_ml: calculations.total_annual_volume_ml }
          : {}),
        ...(typeof calculations.total_annual_toxicity_burden === "number"
          ? { toxicity_burden: calculations.total_annual_toxicity_burden }
          : {}),
        ...(typeof calculations.annual_units_procured === "number"
          ? { units: calculations.annual_units_procured }
          : {}),
        ...(typeof calculations.baseline_annual_cost === "number"
          ? { cost: calculations.baseline_annual_cost }
          : {}),
        ...(typeof calculations.baseline_annual_waste_kg === "number"
          ? { waste: calculations.baseline_annual_waste_kg }
          : {}),
        ...(typeof calculations.baseline_annual_emissions_kgco2e === "number"
          ? { emissions: calculations.baseline_annual_emissions_kgco2e }
          : {}),
      };
    }

    return successResponse.results ?? {};
  }

  static async submitLabWasteCalculator(
    labId: string,
    level: CalculatorLevel,
    notApplicable?: boolean,
    // formData?: FormData
  ): Promise<LabWasteSubmissionResponse> {
    const params = new URLSearchParams({
      level: level,
    });

    const body =
      typeof notApplicable === "boolean"
        ? { not_applicable: notApplicable }
        : undefined;
    const response = (await authApi(
      `/calculator/${labId}/${encodeURIComponent("Lab Waste")}?${params}`,
      {
        method: "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      },
    )) as LabWasteSubmissionResponse | GenericErrorResponse;

    const success = (response as { success?: boolean }).success === true;
    if (!success) {
      const errorResponse = response as GenericErrorResponse;
      throw new Error(
        errorResponse.error || "Failed to submit lab waste calculator",
      );
    }

    return response as LabWasteSubmissionResponse;
  }

  /**
   * Undo a previously set "Not Applicable" status for a category
   * @param labId - The lab ID
   * @param category - The category name
   * @returns Updated status and progress after undo
   */
  static async undoNotApplicable(
    labId: string,
    category: string,
    level: CalculatorLevel,
  ): Promise<{
    success: boolean;
    status: string;
    progress: number;
  }> {
    const params = new URLSearchParams({
      level: level,
    });

    const response = (await authApi(
      `/calculator/${labId}/${encodeURIComponent(category)}/not-applicable?${params}`,
      { method: "DELETE" },
    )) as
      | (AuthResponse & { status?: string; progress?: number })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to undo not applicable");
    }

    return {
      success: true,
      status: response.status || "IN_PROGRESS",
      progress: response.progress ?? 0,
    };
  }

  /**
   * Mark a category as "Not Applicable"
   * @param labId - The lab ID
   * @param category - The category name
   * @returns Updated status and progress
   */
  static async markNotApplicable(
    labId: string,
    category: string,
    level: CalculatorLevel,
  ): Promise<{
    success: boolean;
    status: string;
    progress: number;
  }> {
    const params = new URLSearchParams({
      level: level,
    });

    const response = (await authApi(
      `/calculator/${labId}/${encodeURIComponent(category)}?${params}`,
      {
        method: "POST",
        body: JSON.stringify({ not_applicable: true }),
      },
    )) as
      | (AuthResponse & { status?: string; progress?: number })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to mark as not applicable");
    }

    return {
      success: true,
      status: response.status || "NOT_APPLICABLE",
      progress: response.progress ?? 100,
    };
  }
}
